import React, { useEffect, useState, useRef } from 'react'
import Grid from "@mui/material/Grid"
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import { useGetGpsLogsQuery, useUpdateCommandMutation } from '../../store/services/reportsService'
import ElegantLoader from '../../Components/Loader'
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Chip } from '@mui/material'
import logo from '../../Images/logo.png'
import Navbar from '../../Components/Navbar'

const UpdatedGpsDataLog = () => {
const gpsDataColumns = [
    { field: 'timestamp', header: 'Timestamp' },
    { field: 'rawData', header: 'Raw Data' }
];

    const [inputValue, setInputValue] = useState("")
    const [searchQuery, setSearchQuery] = useState("")
    const [hasSearched, setHasSearched] = useState(false)
    const [commandValue, setCommandValue] = useState("")
    const [isAutoRefresh, setIsAutoRefresh] = useState(false)
    const intervalRef = useRef(null)
    const {data:gpsLogs,error,isLoading:gpsLogsLoading,refetch}=useGetGpsLogsQuery(searchQuery, {
        skip: !searchQuery
    })
    const [updateCommand, { isLoading: isCommandLoading }] = useUpdateCommandMutation()

    // Auto-refresh effect
    useEffect(() => {
        if (isAutoRefresh && searchQuery) {
            const startAutoRefresh = () => {
                intervalRef.current = setInterval(() => {
                    refetch();
                }, 10000);
            };

            startAutoRefresh();

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }
    }, [isAutoRefresh, searchQuery, refetch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const parseGpsData = (text) => {
        try {
            console.log('Raw data received:', text);
            const dataString = text.data;

            if (!dataString) {
                return [];
            }

            const parsedData = JSON.parse(dataString);
            console.log('Parsed JSON data:', parsedData);

            const transformedData = parsedData.map(item => ({
                id: item.pk,
                timestamp: item.fields.timestamp,
                rawData: item.fields.raw_data,
                gpsDataArray: item.fields.raw_data.split(',')
            }));
            return transformedData;
        } catch (error) {
            console.error('Error parsing GPS data:', error);
            return [];
        }
    }

    const handleInputChange = (e) => {
        setInputValue(e.target.value)
    }

    const handleSearch = (event) => {
        event.preventDefault();
        if (inputValue.trim()) {
            setSearchQuery(inputValue.trim());
            setHasSearched(true);
            setIsAutoRefresh(true); // Automatically start auto-refresh when searching
            console.log('Searching for IMEI:', inputValue);
        }
    };

    const handleCommandChange = (e) => {
        setCommandValue(e.target.value)
    }

    const handleSendCommand = async (e) => {
        e.preventDefault()
        if (commandValue.trim() && searchQuery.trim()) {
            try {
                const result = await updateCommand({
                    imei: searchQuery,
                    cmd: commandValue.trim()
                }).unwrap()
                console.log('Command sent successfully:', result)
                setCommandValue('')
                // You can add a success notification here
            } catch (error) {
                console.error('Failed to send command:', error)
                // You can add an error notification here
            }
        }
    }
    

    return (
        <>
            <Navbar page="gps-logs" />
            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
                {/* Header */}
                <Card sx={{ mb: 3, backgroundColor: '#1E293B', color: 'white', borderRadius: 3 }}>
                    <CardContent sx={{ py: 3 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                GPS Data Logs
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                                Track and monitor GPS device location data in real-time
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

            <Grid container spacing={3}>
                {/* Search Section */}
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: '#1E293B', fontWeight: 600 }}>
                                Device Search
                            </Typography>
                            <form onSubmit={handleSearch}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            label="Enter Device IMEI"
                                            placeholder="e.g., 123456789012345"
                                            value={inputValue}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    '&:hover fieldset': {
                                                        borderColor: '#2196F3',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#2196F3',
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            startIcon={<SearchIcon />}
                                            sx={{
                                                backgroundColor: '#1E293B',
                                                borderRadius: 2,
                                                py: 1.5,
                                                fontSize: '1rem',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    backgroundColor: '#334155',
                                                }
                                            }}
                                        >
                                            Search Device
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>
            
                {/* Command Section */}
                {hasSearched && (
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <SendIcon sx={{ color: '#2196F3' }} />
                                    <Typography variant="h6" sx={{ color: '#1E293B', fontWeight: 600 }}>
                                        Send Command
                                    </Typography>
                                    <Chip 
                                        label={`IMEI: ${searchQuery}`} 
                                        size="small" 
                                        sx={{ ml: 'auto', bgcolor: '#f5f5f5', color: '#1E293B' }}
                                    />
                                </Box>
                                <form onSubmit={handleSendCommand}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={8}>
                                            <TextField
                                                fullWidth
                                                label="Command"
                                                placeholder="Enter command to send to device"
                                                value={commandValue}
                                                onChange={handleCommandChange}
                                                variant="outlined"
                                                disabled={!searchQuery || isCommandLoading}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        '&:hover fieldset': {
                                                            borderColor: '#2196F3',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#2196F3',
                                                        },
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                fullWidth
                                                startIcon={<SendIcon />}
                                                disabled={!commandValue.trim() || !searchQuery || isCommandLoading}
                                                sx={{
                                                    backgroundColor: isCommandLoading ? '#bdc3c7' : '#2196F3',
                                                    borderRadius: 2,
                                                    py: 1.5,
                                                    fontSize: '1rem',
                                                    fontWeight: 500,
                                                    '&:hover': {
                                                        backgroundColor: isCommandLoading ? '#bdc3c7' : '#1976D2',
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: '#bdc3c7',
                                                    }
                                                }}
                                            >
                                                {isCommandLoading ? 'Sending...' : 'Send Command'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            
                {/* Data Display Section */}
                <Grid item xs={12}>
                    {!hasSearched ? (
                        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
                            <CardContent>
                                <SearchIcon sx={{ fontSize: 60, color: '#2196F3', mb: 2 }} />
                                <Typography variant="h5" sx={{ mb: 2, color: '#1E293B', fontWeight: 600 }}>
                                    Ready to Search
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                                    Enter a device IMEI in the search box above to view GPS data logs and send commands
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : gpsLogsLoading ? (
                        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 4 }}>
                            <CardContent>
                                <ElegantLoader variant="circular" text="Loading GPS data..." transparent/>
                            </CardContent>
                        </Card>
                    ) : error ? (
                        <Card sx={{ borderRadius: 3, bgcolor: '#ffebee' }}>
                            <CardContent>
                                <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                                    Error Loading Data
                                </Typography>
                                <Typography color="text.secondary">
                                    {error.message || 'Unknown error occurred'}
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        (() => {
                            const parsedRows = parseGpsData(gpsLogs);
                            console.log('Final rows for table:', parsedRows);
                            return (
                                <Card sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Device IMEI: {searchQuery}
                                            </Typography>
                                            <Chip 
                                                label={`${parsedRows.length} Records`} 
                                                sx={{ ml: 'auto', bgcolor: '#e3f2fd', color: '#2196F3' }}
                                            />
                                        </Box>
                                        
                                        {parsedRows.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <SearchIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                                                <Typography variant="h6" sx={{ mb: 1, color: '#1E293B' }}>
                                                    No Data Found
                                                </Typography>
                                                <Typography color="text.secondary">
                                                    No GPS data found for IMEI: {searchQuery}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Paper elevation={1} sx={{ 
                                                overflow: 'hidden', 
                                                borderRadius: 3
                                            }}>
                                                <TableContainer>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ 
                                                                    width: '200px', 
                                                                    fontWeight: 600, 
                                                                    backgroundColor: '#1E293B',
                                                                    color: 'white',
                                                                    padding: '16px'
                                                                }}>
                                                                    Timestamp
                                                                </TableCell>
                                                                <TableCell sx={{ 
                                                                    fontWeight: 600, 
                                                                    backgroundColor: '#1E293B',
                                                                    color: 'white',
                                                                    padding: '16px'
                                                                }}>
                                                                    Raw Data
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {parsedRows.map((row, index) => (
                                                                <TableRow 
                                                                    key={row.id || index} 
                                                                    hover
                                                                    sx={{
                                                                        '&:hover': {
                                                                            backgroundColor: '#f5f5f5'
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell sx={{ 
                                                                        width: '200px', 
                                                                        padding: '12px 16px',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {row.timestamp}
                                                                    </TableCell>
                                                                    <TableCell sx={{ 
                                                                        padding: '12px 16px',
                                                                        wordBreak: 'break-all',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {row.rawData}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Paper>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })()
                    )}
                </Grid>
            </Grid>
        </Box>
        </>
    )
}

export default UpdatedGpsDataLog
